// COMUTATORUL unei etape: panoul plutitor care se deschide din ▾ pe segmentul
// lanțului sau din «Schimbă ▾» din meniul vizualizatorului — același panou,
// care se închide după alegere (A3, artboard-ul «Comutator»).
//
// Sus: titlul etapei cu numărătorile, comutarea «Pe [amonte] (n) | Toate (N)».
// Apoi grupele, pe amonte — grupul firului curent primul, marcat —, fiecare
// rând cu fazele, starea și ce stă în aval. Jos: «Importă arhitectură (DWG)»
// sau «+ variantă», și «Toate soluțiile →».
//
// Clicul pe un rând mută FIRUL: dacă ecranul e al unei etape din aval, rămâi
// pe ea, pe soluția implicită din noul amonte («Alegi altă arhitectură:
// Situația trece pe amplasarea ei, iar Structura pe cea mai nouă soluție gata
// de pe ea»); pe Verificări și Costuri rămâi pe ele, pe noul fir (A5; v.
// `selectie.caleDupaAlegere`). «Deschide», pe rândul de sub cursor, deschide
// chiar rândul ales.
//
// Panoul e un dialog modal: ia focusul la deschidere, Escape îl închide, iar
// focusul se întoarce pe butonul care l-a deschis.

import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { useT } from "../i18n";
import FazeBara from "../components/FazeBara";
import { useRolProiect } from "../components/rol";
import { potPleca } from "../nescris";
import { useRegistru } from "./Registru";
import { useActiuni } from "./actiuni";
import type { Disciplina } from "./api";
import { useFir } from "./useFir";
import {
  caleDupaAlegere, candRand, caleRand, caleSolutii, fazeleElementului, fazeleRandului,
  firulRandului, grupeComutator, numaratori, numarElement, numarRand, numeElement, numeRand,
  poateGeneraSolutia,
  type RandComutator,
} from "./selectie";
import "./nav.css";

export default function Comutator({ disciplina, ancora, laInchide }: {
  disciplina: Disciplina;
  /** dreptunghiul de sub care se deschide */
  ancora: DOMRect | null;
  laInchide: () => void;
}) {
  const t = useT();
  const navigate = useNavigate();
  const { registru, projectId } = useRegistru();
  const { ruta, fir, rand: randPrivit } = useFir();
  const { potScrie } = useRolProiect();
  const actiuni = useActiuni();
  const [toate, setToate] = useState(true);
  const panou = useRef<HTMLDivElement>(null);

  // `laInchide` vine nou la fiecare desenare a părintelui: ținut într-un ref,
  // efectul rulează o singură dată pe deschidere — altfel focusul ar sări
  // înapoi pe panou (și pe buton, la curățare) la fiecare sondaj al registrului.
  const inchide = useRef(laInchide);
  inchide.current = laInchide;
  useEffect(() => {
    const deUnde = document.activeElement as HTMLElement | null;
    const taste = (e: KeyboardEvent) => { if (e.key === "Escape") inchide.current(); };
    document.addEventListener("keydown", taste);
    panou.current?.focus();
    return () => {
      document.removeEventListener("keydown", taste);
      if (deUnde?.isConnected) deUnde.focus();
    };
  }, []);

  const amonteDisc = disciplina === "architecture" ? "site_plan"
    : disciplina === "structure" ? "architecture" : null;
  const amonte = amonteDisc && registru
    ? registru.solutii.find((s) => s.id === fir[amonteDisc]) ?? null : null;
  const grupe = useMemo(
    () => (registru ? grupeComutator(registru, disciplina, fir, !toate && !!amonte) : []),
    [registru, disciplina, fir, toate, amonte]);
  const peAmonte = useMemo(
    () => (registru && amonte
      ? grupeComutator(registru, disciplina, fir, true)
          .reduce((n, g) => n + g.randuri.length, 0) : 0),
    [registru, disciplina, fir, amonte]);

  if (!registru) return null;
  const nr = numaratori(registru);
  const n = disciplina === "structure" ? nr.structure : nr[disciplina];

  const alege = (r: RandComutator, direct: boolean) => {
    if (!potPleca()) return;
    const cale = direct ? caleRand(projectId, r.rand)
      : caleDupaAlegere(projectId, ruta, randPrivit?.discipline ?? null, r.rand,
                        firulRandului(registru, r.rand), registru.solutii);
    laInchide();
    navigate(cale);
  };

  const titlu = t(`comutator.titlu.${disciplina}` as const);
  const parti = [String(n.total)];
  if (n.gata) parti.push(t("comutator.gata", { n: n.gata }));
  if (n.inLucru) parti.push(t("lant.in_lucru", { n: n.inLucru }));
  if (n.asteapta) parti.push(t("lant.asteapta", { n: n.asteapta }));
  if (n.nuTrec) parti.push(t("lant.nu_trec", { n: n.nuTrec }));

  // Sub segment, cât încape pe ecran: 560 lățime, lipit de marginea din
  // dreapta când segmentul e prea la dreapta.
  const LATIME = 560;
  const stanga = Math.max(8, Math.min((ancora?.left ?? 72),
                                      window.innerWidth - LATIME - 8));
  const sus = (ancora?.bottom ?? 56) + 6;

  const rand = (r: RandComutator) => {
    const faze = r.element ? fazeleElementului(r.element) : fazeleRandului(r.rand);
    // titlul panoului spune deja disciplina («Arhitecturi»), deci rândul
    // poartă doar numărul; numele întreg stă în `title`
    const nume = r.element ? numarElement(r.element) : numarRand(r.rand);
    const numeIntreg = r.element ? numeElement(r.element) : numeRand(r.rand);
    const editata = r.element?.fel === "solutie" && r.element.macro.structura?.editare;
    const cand = candRand(r.element?.fel === "solutie" ? r.element.macro : r.rand);
    return (
      <div key={r.id} className={`com-rand${r.curent ? " curent" : ""}`}>
        <button type="button" className="com-alege" title={numeIntreg}
                onClick={() => alege(r, false)}>
          <span className="com-nume">
            <b>{nume}</b>
            {r.rand.promoted && <span className="nav-chip acc">★</span>}
            {r.curent && <span className="nav-chip acc">{t("comutator.curenta")}</span>}
            {editata && (
              <span className="nav-chip met">
                {t("bloc.editata_blocate", { n: editata.blocate })}
              </span>
            )}
            {cand && <span className="com-cand" title={cand.titlu}>{cand.text}</span>}
          </span>
          <FazeBara faze={faze} orientare="orizontal" />
          {r.aval && <span className="com-aval">{r.aval}</span>}
        </button>
        <button type="button" className="btn btnp com-deschide"
                onClick={() => alege(r, true)}>{t("comutator.deschide")}</button>
      </div>
    );
  };

  const arhitecturaCurenta = fir.architecture;
  const poateGenera = disciplina === "structure"
    && poateGeneraSolutia(registru.solutii, arhitecturaCurenta);

  return createPortal(
    <div className="ob nav-voal" onMouseDown={(e) => { if (e.target === e.currentTarget) laInchide(); }}>
      <div ref={panou} tabIndex={-1} role="dialog" aria-modal="true" aria-label={titlu}
           className="card com-panou" style={{ left: stanga, top: sus, width: LATIME }}>
        <div className="com-cap">
          <h2>{titlu}</h2>
          <span className="com-cifre">{parti.join(" · ")}</span>
          <button type="button" className="btn btng com-x" aria-label={t("comutator.inchide")}
                  onClick={laInchide}>×</button>
        </div>
        {amonte && (
          <div className="seg com-filtru" role="group" aria-label={t("comutator.filtru")}>
            <button type="button" className={!toate ? "on" : ""} aria-pressed={!toate}
                    onClick={() => setToate(false)}>
              {t("comutator.pe", { solutie: numeRand(amonte), n: peAmonte })}
            </button>
            <button type="button" className={toate ? "on" : ""} aria-pressed={toate}
                    onClick={() => setToate(true)}>
              {t("comutator.toate", { n: n.total })}
            </button>
          </div>
        )}
        <div className="com-grupe scroll">
          {grupe.map((g) => (
            <div key={g.cheie} className="com-grup">
              <span className={`lbl${g.curent ? " curent" : ""}`}>
                {g.curent && amonte ? t("comutator.fir_curent", { eticheta: g.eticheta })
                  : g.eticheta}
              </span>
              {g.randuri.length ? g.randuri.map(rand) : (
                <span className="com-gol">
                  {g.cheie === "importate" ? t("comutator.fara_importate")
                    : t("comutator.gol")}
                </span>
              )}
            </div>
          ))}
        </div>
        {disciplina !== "site_plan" && (
          <div className="com-nota">{t(`comutator.nota.${disciplina}` as const)}</div>
        )}
        {actiuni.eroare && <p className="err">{actiuni.eroare}</p>}
        <div className="com-subsol">
          {disciplina === "architecture" && (
            <Link className="btn" to={`/p/${projectId}/dossier`} onClick={laInchide}>
              {t("comutator.importa")}
            </Link>
          )}
          {poateGenera && arhitecturaCurenta && (
            <button type="button" className="btn btnp" disabled={!potScrie || !!actiuni.ocupat}
                    onClick={() => { void actiuni.genereazaSolutia(arhitecturaCurenta)
                                       .then(laInchide); }}>
              {t("actiune.genereaza")}
            </button>
          )}
          <Link className="com-toate" to={caleSolutii(projectId)} onClick={laInchide}>
            {t("comutator.toate_solutiile")}
          </Link>
        </div>
      </div>
    </div>,
    document.body,
  );
}
